import { supabase, isConfigured } from './supabase.js';
import { currentUser } from './auth.js';
import { emit, on } from '../state/bus.js';

/* What the current session owns — a credit balance, whether it has ever
   purchased, and until when a pack's 12 months run. The first two live on
   `profiles`, written only by the database itself (a client can update its
   own row but is RLS-blocked from touching either column — see
   0004_billing.sql); the date is computed by the database from the ledger,
   which no client can write at all. So this module is a read-only cache of
   server truth, never something that computes or grants entitlements on its
   own.

   Refreshed on 'auth:changed' (boot, sign-in, the anonymous→email upgrade)
   and again whenever something spends or grants a credit, each of which
   fires 'entitlements:changed' so every dependent bit of UI — the credit
   chip, the flat-view watermark, locked presets/templates rows — updates
   together without a page reload. */

const EMPTY = { credit_balance: 0, credits_ever_purchased: false, pack_until: null };
let state = { ...EMPTY };

export const creditBalance = () => state.credit_balance;
export const hasCredits = () => state.credit_balance > 0;
export const hasEverPurchased = () => state.credits_ever_purchased;

/* WHAT A PACK UNLOCKS LASTS 12 MONTHS — watermarked video, cloud projects,
   commercial use — counted from the last time credits were added, whatever
   has been spent since (0030_pack_access_12_months.sql). Asked of the
   database's own pack_access_until() rather than computed here, so the date
   the app shows and the date the cloud-project policy enforces are one
   answer, not two that could drift. */
export const packUntil = () => (state.pack_until ? new Date(state.pack_until) : null);
export const hasActivePack = () => { const d = packUntil(); return !!d && d > new Date(); };

export async function loadEntitlements() {
  const user = currentUser();
  if (!isConfigured || !user) {
    state = { ...EMPTY };
    emit('entitlements:changed', state);
    return state;
  }
  /* Both requests start before either is awaited, so they run side by side. */
  const profileReq = supabase.from('profiles').select('credit_balance, credits_ever_purchased').eq('id', user.id).single();
  const accessReq = supabase.rpc('pack_access_until');
  const { data, error } = await profileReq;
  const access = await accessReq;
  if (!error && data) state = { ...state, ...data };
  /* A failed date lookup keeps whatever was known rather than locking the
     account out on a network blip — the database policy is what actually
     refuses a lapsed cloud save, not this. */
  if (!access.error) state = { ...state, pack_until: access.data ?? null };
  emit('entitlements:changed', state);
  return state;
}

/**
 * The account's credit ledger, newest first — every purchase, every credit the
 * owner has granted, and every export that spent some.
 *
 * Readable by its owner and nobody else (credit_tx_read_own, 0004), and
 * writable by nobody at all from a client, so what this returns is the
 * database's own record of how the balance got where it is rather than
 * something the app tells you. That is what makes it worth showing: a balance
 * you cannot account for is a balance you do not trust.
 */
export async function listCreditHistory(limit = 100) {
  const user = currentUser();
  if (!isConfigured || !user) return [];
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('id, delta, reason, balance_after, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

/** Applied straight from an RPC's return value — no round trip needed. */
export function setCreditBalance(n) {
  state = { ...state, credit_balance: n };
  emit('entitlements:changed', state);
}

on('auth:changed', () => { loadEntitlements(); });
